import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { AlertCircle, ExternalLink } from 'lucide-react';
import moment from 'moment';

const PendingTodosWidget = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingTodos();
  }, []);

  const fetchPendingTodos = async () => {
    try {
      const response = await api.get('/todos', {
        params: {
          status: 'Pending',
          limit: 5,
        },
      });
      setTodos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Completed' || status === 'Cancelled') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const getDaysOverdue = (dueDate) => {
    const days = moment().diff(moment(dueDate), 'days');
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    if (days < 365) {
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: 'bg-purple-100 text-purple-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      Task: 'bg-purple-100 text-purple-800',
      Review: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const pendingTodos = todos.filter(t => t.status === 'Pending' || t.status === 'In Progress');

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Pending To-Do's</h2>
          <Link
            to="/todos"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View All
          </Link>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {pendingTodos.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No pending to-dos</p>
          </div>
        ) : (
          pendingTodos.map((todo) => {
            const overdue = isOverdue(todo.dueDate, todo.status);
            const daysOverdue = overdue ? getDaysOverdue(todo.dueDate) : null;

            return (
              <div
                key={todo._id}
                className={`p-6 hover:bg-gray-50 transition-colors ${
                  overdue ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Red Exclamation Mark for Overdue */}
                  {overdue && (
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3 flex-wrap">
                        {todo.caseId && (
                          <span className="text-sm font-medium text-gray-600">
                            {todo.caseId.caseNumber || 'Case'}
                          </span>
                        )}
                        {todo.flagId && (
                          <span className="text-sm font-medium text-gray-600">
                            {todo.flagId.flagNumber || 'Flag'}
                          </span>
                        )}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            'Task'
                          )}`}
                        >
                          Task
                        </span>
                        {overdue && (
                          <span className="text-sm text-gray-600 font-medium">
                            Due {daysOverdue}
                          </span>
                        )}
                      </div>
                      {todo.caseId && (
                        <Link
                          to={`/cases/${todo.caseId._id}`}
                          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      )}
                    </div>

                    <h3 className="text-base font-medium text-gray-900 mb-2">
                      {todo.title}
                    </h3>

                    {todo.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {todo.description}
                      </p>
                    )}

                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        Created: {moment(todo.createdAt).format('DD-MMM-YYYY hh:mm A')}
                      </span>
                      <span>
                        Updated: {moment(todo.updatedAt).format('DD-MMM-YYYY hh:mm A')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PendingTodosWidget;


